/**
 * Where a card goes after you act on it, until recomputation lands (M4.3,
 * COR-65). This is deliberately not a second interpretation of the log: it
 * reads the appended corrections through `correctionEffect` — the pipeline's
 * own table — and asks only the one question the screen needs answered now,
 * which is which section the card belongs in.
 *
 * Nothing here edits anything. The corrections stay in the log, the ledger
 * stays as the run produced it, and the next run recomputes from evidence
 * plus the log. What the screen shows in between is this overlay, and it says
 * so on the card.
 */
import type {
  Correction,
  IdentityGraph,
  LedgerItem,
  LedgerItemId,
  Status,
  Surface,
} from '@cordelia/core'
import { correctionEffect } from '@cordelia/pipeline'

export type LocalRouting = {
  /** Where the item sits until the next run. */
  readonly surface: Surface
  readonly status: Status
  /** What you did, in the product's voice. */
  readonly note: string
  /**
   * How to take it back. There is no delete: `unsuppress` appends a
   * correction lifting the named suppression, and `reopen` appends a later
   * observation that the item is still open — which is what the table's
   * "Still open" does, and the only undo an append-only log can have.
   */
  readonly undo: 'unsuppress' | 'reopen' | null
  /** The suppression `unsuppress` would lift. */
  readonly suppression_id: string | null
}

const SUPPRESSION_NOTE: Readonly<Record<string, string>> = {
  'user:dismiss': 'You dismissed this.',
  'user:irrelevant_or_private': 'You dismissed this as irrelevant or private.',
  'user:not_a_commitment': 'You said this is not a commitment.',
  'user:draft_abandoned': 'You said that draft was abandoned on purpose.',
}

/** Still open: back to the active ledger, on the side the parties put it. */
function activeSurface(item: LedgerItem, identity: IdentityGraph): Surface {
  if (item.owed_by === 'unresolved') return 'may-be-expected'
  return item.owed_by === identity.user ? 'you-owe' : 'waiting-on'
}

/** The corrections that name this item, oldest first. */
export function correctionsFor(
  item: Pick<LedgerItem, 'id' | 'claim_id'>,
  corrections: readonly Correction[],
): readonly Correction[] {
  const names: readonly string[] = [item.id, item.claim_id]
  return [...corrections]
    .filter((c) => c.affects.some((a) => names.includes(a)))
    .sort((a, b) => Date.parse(a.time) - Date.parse(b.time))
}

/**
 * The overlay for one card, or null when the log says nothing about it. The
 * last effect wins, except that an `unsuppress` lifts the suppression it
 * names and leaves whatever preceded it standing.
 */
export function localRouting(
  item: LedgerItem,
  corrections: readonly Correction[],
  identity: IdentityGraph,
): LocalRouting | null {
  let routing: LocalRouting | null = null
  for (const correction of correctionsFor(item, corrections)) {
    const effect = correctionEffect(correction)
    switch (effect.kind) {
      case 'suppress':
        routing = {
          surface: 'suppressed',
          status: item.status,
          note: SUPPRESSION_NOTE[effect.suppression.reason] ?? 'You dismissed this.',
          undo: 'unsuppress',
          suppression_id: effect.suppression.id,
        }
        break
      case 'unsuppress':
        if (routing?.suppression_id === effect.suppression_id) routing = null
        break
      case 'observation':
        routing =
          effect.openness === 'open'
            ? {
                surface: activeSurface(item, identity),
                status: item.status,
                note: 'You said this is still open.',
                undo: null,
                suppression_id: null,
              }
            : {
                surface: 'history',
                status: effect.openness === 'cancelled' ? 'cancelled' : 'fulfilled',
                note:
                  effect.openness === 'cancelled'
                    ? 'You dropped this. It is resolved, not hidden.'
                    : 'You marked this done.',
                undo: 'reopen',
                suppression_id: null,
              }
        break
      case 'transition':
        // Only delegation moves a card before M4.3; the rest are the
        // correction sheet's, and recomputation decides where they land.
        if (effect.type === 'delegation')
          routing = {
            surface: 'history',
            status: 'delegated',
            note: 'You handed this on. Who made the promise is unchanged.',
            undo: 'reopen',
            suppression_id: null,
          }
        break
      case 'confirmation':
        // The classification is a record, not a route: the observation or the
        // delegation appended alongside it is what moves the card.
        break
    }
  }
  // An overlay that puts the card back exactly where the run put it is not an
  // overlay: reopening an active card leaves nothing for the screen to say.
  if (routing !== null && routing.undo === null && routing.surface === item.surface) return null
  return routing
}

/** Every card the log has something to say about, by item id. */
export function localRoutingByItem(
  items: readonly LedgerItem[],
  corrections: readonly Correction[],
  identity: IdentityGraph,
): ReadonlyMap<LedgerItemId, LocalRouting> {
  const out = new Map<LedgerItemId, LocalRouting>()
  if (corrections.length === 0) return out
  for (const item of items) {
    const routing = localRouting(item, corrections, identity)
    if (routing) out.set(item.id, routing)
  }
  return out
}
